import { GetterTree } from "vuex";
import { RootState } from "../store";
import { ExploreV2State } from "./ExploreV2Module";
import { MeasurementPrefUtility } from "../../../../utils/helpers/MeasurementPref";
import { communityRangeClause } from "../../../../utils/helpers/communityRangeFilter";
import  { distance as turfDistance } from "@turf/turf";
import { SymbolLayer } from "mapbox-gl";

const getters: GetterTree<ExploreV2State, RootState> = {
  initialMapSettings(state) {
    return {
      container: 'map', // container ID
      // @ts-ignore
      center: state.center, // starting position [lng, lat]
      zoom: state.zoom ?? 0, // starting zoom
      attributionControl: false,
      maxPitch: 85,
    }
  },
  clusterLayer(state) {
    return {
      id: 'clusters',
      type: 'circle',
      source: 'trails',
      filter: ['has', 'point_count'],
      paint: {
        'circle-blur': .2,
        'circle-stroke-color': '#FFCD16',
        'circle-stroke-width': 5,
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#ffffff',
          100,
          '#f1f075',
          750,
          '#f28cb1'
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20,
          100,
          30,
          750,
          40
        ]
      }
    }
  },
  clusterCountLayer(state): SymbolLayer {
    let layer: SymbolLayer = {
      id: 'cluster-count',
      type: 'symbol',
      source: 'trails',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated} +',
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
    }
    return layer
  },
  hasDistanceFilter(state): boolean {
    return (state.selectedDistanceRangeMax ?? ExploreV2State.distanceRangeMax) < ExploreV2State.distanceRangeMax
  },
  hasCommunityDurationFilter(state): boolean {
    const minHours = state.selectedCommunityDurationRangeMin ?? ExploreV2State.communityDurationRangeMin
    const maxHours = state.selectedCommunityDurationRangeMax ?? ExploreV2State.communityDurationRangeMax
    return minHours > ExploreV2State.communityDurationRangeMin || maxHours < ExploreV2State.communityDurationRangeMax
  },
  hasCommunityElevationFilter(state): boolean {
    const minMeters = state.selectedCommunityElevationRangeMin ?? ExploreV2State.communityElevationRangeMin
    const maxMeters = state.selectedCommunityElevationRangeMax ?? ExploreV2State.communityElevationRangeMax
    return minMeters > ExploreV2State.communityElevationRangeMin || maxMeters < ExploreV2State.communityElevationRangeMax
  },
  hasDifficultyFilter(state): boolean {
    return state.selectedDifficulties.length != 0
  },
  hasSelectedTags(state) {
    return state.selectedTrailTags.length > 0
  },
  hasSelectedCommunityItemTypes(state) {
    return state.selectedCommunityItemTypes.length > 0
  },
  hasSelectedActivityTags(state) {
    return state.selectedActivityTags?.length > 0
  },
  hasSelectedFeatureTags(state) {
    return state.selectedFeatureTags?.length > 0
  },
  reversedActiveTrails(state) {
    return state.selectedTrailTags.slice().reverse()
  },
  hasElevationsFilter(state): boolean {
    return state.selectedElevations.length != 0
  },
  hasDurationFilter(state): boolean {
    return (state.selectedDurationRangeMin ?? 0) > 0 ||
      (state.selectedDurationRangeMax !== null && state.selectedDurationRangeMax < 24)
  },
  hasTrailGeometryTypeFilter(state): boolean {
    return state.selectedTrailGeometryTypes?.length > 0
  },
  unclusteredPointLayer(state) {
    return {
      id: 'unclustered-point',
      type: 'circle',
      source: 'trails',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#11b4da',
        'circle-radius': 12,
        'circle-stroke-width': 3,
        'circle-stroke-color': '#fff'
      }
    }
  },
  filterSelectedOptionsMaxDistance(state) {
    let measurementPref = MeasurementPrefUtility.preference()
    // @ts-ignore
    return measurementPref.bigUnitFormatter(state.filterSelectedOptions.selectedDistanceRangeMax).toString()
  },
  hasfilter(state) {
    const maxDistance = state.selectedDistanceRangeMax ?? ExploreV2State.distanceRangeMax;
    const minDistance = state.selectedDistanceRangeMin ?? ExploreV2State.distanceRangeMin;
    const distanceActive =
      minDistance > ExploreV2State.distanceRangeMin ||
      maxDistance < ExploreV2State.distanceRangeMax;

    if (state.searchSource === 'community') {
      const communityDurationActive =
        state.selectedCommunityDurationRangeMin > ExploreV2State.communityDurationRangeMin ||
        state.selectedCommunityDurationRangeMax < ExploreV2State.communityDurationRangeMax
      const communityElevationActive =
        state.selectedCommunityElevationRangeMin > ExploreV2State.communityElevationRangeMin ||
        state.selectedCommunityElevationRangeMax < ExploreV2State.communityElevationRangeMax

      return distanceActive ||
        state.selectedCommunityItemTypes.length > 0 ||
        communityDurationActive ||
        communityElevationActive
    }

    return distanceActive ||
      state.selectedTrailTags.length > 0 ||
      state.selectedActivityTags?.length > 0 ||
      state.selectedFeatureTags?.length > 0 ||
      state.selectedElevations.length > 0 ||
      state.selectedDifficulties.length > 0 ||
      state.selectedMinRating != null ||
      state.selectedTrailGeometryTypes?.length > 0 ||
      (state.selectedDurationRangeMin ?? 0) > 0 ||
      (state.selectedDurationRangeMax !== null && state.selectedDurationRangeMax < 24);
  },
  getTrailGeoJsonFeature(state) {
    // Return the full FeatureCollection
    if (state.trail == null) {
      return null
    }
    return JSON.parse(state.trail.geojsonRoutesOnlyString)
  },
  getRecordingGeoJsonFeature(state) {
    if (state.recording == null) {
      return null
    }
    const lineString = state.recording.lineString
    if (!lineString) return null
    const parsed = typeof lineString === 'string' ? JSON.parse(lineString) : lineString
    const hasElevation = (state.recording.elevationGainMeters ?? 0) > 0

    // Normalize coordinates to [lon, lat] or [lon, lat, altitude]
    // The backend may include distance as a 3rd or 4th value which must be stripped
    const coords = parsed.features?.[0]?.geometry?.coordinates
    if (coords && coords.length > 0) {
      parsed.features[0].geometry.coordinates = coords.map((c: number[]) => {
        if (hasElevation && c.length >= 3) {
          return [c[0], c[1], c[2]] // [lon, lat, altitude]
        }
        return [c[0], c[1]] // [lon, lat] only
      })

      const normalizedCoords = parsed.features[0].geometry.coordinates
      parsed.features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: normalizedCoords[0] },
        properties: { markerType: 'start' }
      })
      parsed.features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: normalizedCoords[normalizedCoords.length - 1] },
        properties: { markerType: 'end' }
      })
    }
    return parsed
  },
  getTrailMultiLineString(state) {
    // Get just the MultiLineString feature for compatibility
    if (state.trail == null) {
      return null
    }
    let geojson = JSON.parse(state.trail.geojsonRoutesOnlyString)
    // get feature where geometry is a MultiLineString
    return geojson.features.find((feature: any) => feature.geometry.type === "MultiLineString")
  },
  getCountryGeoJsonFeature(state) {
    return JSON.parse(state.country.polygon.boundingPolygon)
  },
  getRegionGeoJsonFeature(state) {
    return state.region.polygon ? JSON.parse(state.region.polygon.boundingPolygon) : null
  },
  createChartData(state, getters) {
    if (state.trail == null) {
      return null
    }
    const coordinates = getters.getTrailMultiLineString.geometry.coordinates
    const chartdata: any = {
      labels: [],
      xLabels: [],
      lat: [],
      lon: [],
      datasets: [
        {
          borderColor: '#ffffff',
          borderWidth: 2,
          fill: false,
          data: [],
        },
      ],
    };
    let divider = 0;
    if (coordinates.length > 1000) {
      divider = Math.round(coordinates.length / 1000);
    }
    let count = 0;
    let distance = 0;
    const distances = coordinates.map((coordinate: any, index: number) => {
      if (index === 0) {
        return 0;
      } else {
        distance += Math.round(turfDistance(coordinates[index - 1], coordinate, { units: 'meters' }))
        return distance;
      }
    })
    const measurementUtils = MeasurementPrefUtility.preference()
    for (let i = 0; i < coordinates.length; i++) {
      if (count === divider) {
        count = 0;
        const coord = coordinates[i];
        chartdata.xLabels.push(measurementUtils.bigUnitFormatter(distances[i] / 1000, false));

        chartdata.labels.push(measurementUtils.smallUnitFormatter(coord[2]));
        chartdata.lat.push(coord[1]);
        chartdata.lon.push(coord[0]);
        chartdata.datasets[0].data.push(measurementUtils.smallUnitValue(coord[2]));
      } else {
        count++;
      }
    }
    return chartdata;
  },
  combineAndUniqueifyDifficulties(state) {
    const combinedDifficulties = [...state.selectedDifficulties, ...state.selectedElevations];
    let a = combinedDifficulties.concat();
    for (let i = 0; i < a.length; ++i) {
      for (let j = i + 1; j < a.length; ++j) {
        if (a[i] === a[j])
          a.splice(j--, 1);
      }
    }
    return a;
  },
  toFilterString(state, getters): string {
    let filterString = ""
    if (state.selectedDistanceRangeMax) {
      filterString += "&&distance_meters:["
      filterString += `${state.selectedDistanceRangeMin * 1000}..`
      if (state.selectedDistanceRangeMax! == ExploreV2State.distanceRangeMax) {
        filterString += `${Math.round((250000 * 1000))}`
      } else {
        filterString += `${Math.round((state.selectedDistanceRangeMax! * 1000))}`
      }
      filterString += "]"
    }

    if (state.searchSource === 'community') {
      filterString += communityRangeClause({
        field: 'duration_minutes',
        selectedMin: state.selectedCommunityDurationRangeMin,
        selectedMax: state.selectedCommunityDurationRangeMax,
        rangeMin: ExploreV2State.communityDurationRangeMin,
        rangeMax: ExploreV2State.communityDurationRangeMax,
        scale: 60,
      })
      filterString += communityRangeClause({
        field: 'elevation_gain_meters',
        selectedMin: state.selectedCommunityElevationRangeMin,
        selectedMax: state.selectedCommunityElevationRangeMax,
        rangeMin: ExploreV2State.communityElevationRangeMin,
        rangeMax: ExploreV2State.communityElevationRangeMax,
      })

      if (state.selectedCommunityItemTypes.length > 0) {
        filterString += "&&("
        for (let i = 0; i < state.selectedCommunityItemTypes.length; i++) {
          filterString += `item_type:=\`${state.selectedCommunityItemTypes[i]}\``
          if (i < state.selectedCommunityItemTypes.length - 1) {
            filterString += " || "
          }
        }
        filterString += ")"
      }
      return filterString
    }

    if (state.selectedMinRating) {
      filterString += `&&average_rating:>=${Math.round(state.selectedMinRating)}`
    }
    let difficulties = getters.combineAndUniqueifyDifficulties
    if (difficulties.length > 0) {
      filterString += "&&("
      for (let i = 0; i < difficulties.length; i++) {
        filterString += `difficulty:=\`${difficulties[i]}\``
        if (i < difficulties.length - 1) {
          filterString += " || "
        }
      }
      filterString += ")"
    }
    // Activity tags filter
    if (state.selectedActivityTags?.length > 0) {
      filterString += "&&activity_tags:["
      for (const tag of state.selectedActivityTags) {
        if (tag == state.selectedActivityTags[state.selectedActivityTags.length - 1]) {
          filterString += `${tag}]`
        } else {
          filterString += `${tag}, `
        }
      }
    }
    // Feature tags filter
    if (state.selectedFeatureTags?.length > 0) {
      filterString += "&&feature_tags:["
      for (const tag of state.selectedFeatureTags) {
        if (tag == state.selectedFeatureTags[state.selectedFeatureTags.length - 1]) {
          filterString += `${tag}]`
        } else {
          filterString += `${tag}, `
        }
      }
    }
    const minDurationHours = state.selectedDurationRangeMin ?? 0
    const maxDurationHours = state.selectedDurationRangeMax ?? 24
    if (minDurationHours > 0 || (state.selectedDurationRangeMax !== null && maxDurationHours < 24)) {
      const minMinutes = Math.round(minDurationHours * 60)
      const maxMinutes = maxDurationHours >= 24 ? 99999 : Math.round(maxDurationHours * 60)
      filterString += `&&duration_minutes:[${minMinutes}..${maxMinutes}]`
    }
    if (state.selectedTrailGeometryTypes?.length > 0) {
      filterString += "&&("
      for (let i = 0; i < state.selectedTrailGeometryTypes.length; i++) {
        filterString += `trail_geometry_type:=\`${state.selectedTrailGeometryTypes[i]}\``
        if (i < state.selectedTrailGeometryTypes.length - 1) {
          filterString += " || "
        }
      }
      filterString += ")"
    }
    return filterString
  }
}

export default getters;
